/**
 * ConfirmFocusCard — surfaced on the learner dashboard.
 *
 * Pulls the next upcoming booking + proposed focus (set by the instructor
 * via the planning flow), and lets the learner:
 *   - ✅ Confirm — locks in the focus, instructor is notified
 *   - 🔄 Request change — opens the lesson-focus form to propose alternatives
 *
 * Only renders when an upcoming booking exists. Stays out of the way once
 * the learner has confirmed (becomes a small "✓ Focus confirmed" pill).
 *
 * Backend:
 *   - GET  /api/learner/next-lesson-focus
 *   - POST /api/learner/confirm-focus
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Calendar, Target, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { formatApiError } from "../utils/apiError";

const API = process.env.REACT_APP_BACKEND_URL;

function _formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "pm" : "am";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}${period}` : `${h12}:${String(m).padStart(2, "0")}${period}`;
}

function _formatDate(d) {
  if (!d) return "";
  const today = new Date();
  const target = new Date(d + "T00:00:00");
  const diffDays = Math.round((target - today.setHours(0, 0, 0, 0)) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays < 7) return target.toLocaleDateString("en-GB", { weekday: "long" });
  return target.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export function ConfirmFocusCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/reflections/learner/next-lesson-focus`, { withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !data?.has_upcoming) return null;

  const onConfirm = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/reflections/learner/confirm-focus`,
        { booking_id: data.booking_id, action: "confirm" },
        { withCredentials: true }
      );
      setData({ ...data, agreed_by_learner: true });
      toast.success("Focus confirmed — your instructor will see this in-app.");
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't confirm right now."));
    } finally {
      setSubmitting(false);
    }
  };

  // "Request a change" button removed — the /lesson-focus route doesn't exist.
  // Learner will message instructor directly via the messages thread instead.

  // Confirmed state — slim pill.
  if (data.agreed_by_learner) {
    return (
      <div data-testid="confirm-focus-confirmed" className="rounded-xl bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200/70 dark:border-emerald-800/40 px-4 py-3 flex items-center gap-2.5">
        <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
        <span className="text-[13px] text-emerald-800 dark:text-emerald-200 font-medium">
          Focus confirmed for {_formatDate(data.date)} {_formatTime(data.time)} — see you then.
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="confirm-focus-card"
      className="rounded-2xl bg-white dark:bg-slate-900 border border-amber-200/80 dark:border-amber-700/40 shadow-sm p-5"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
          <Target className="w-5 h-5 text-amber-700 dark:text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10.5px] uppercase tracking-[0.2em] text-amber-700 dark:text-amber-400 font-bold mb-0.5">
            Next lesson focus
          </p>
          <h3 className="text-base font-bold text-slate-900 dark:text-white leading-tight" data-testid="confirm-focus-title">
            {data.instructor_name} wants to work on:
          </h3>
          <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {_formatDate(data.date)} at {_formatTime(data.time)} · {data.duration} min
          </p>
        </div>
      </div>

      {data.has_focus ? (
        <ul data-testid="confirm-focus-list" className="space-y-1.5 mb-4 ml-1">
          {data.proposed_focus.map((f, i) => (
            <li key={i} className="text-sm text-slate-700 dark:text-slate-200 flex items-start gap-2">
              <span className="text-amber-600 dark:text-amber-400 font-bold leading-tight">·</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p data-testid="confirm-focus-empty" className="text-[13px] text-slate-500 dark:text-slate-400 mb-4">
          No specific focus has been set yet. You can suggest what you'd like to work on.
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        {data.has_focus && (
          <Button
            data-testid="confirm-focus-confirm-btn"
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 h-11 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" />Confirm focus</>}
          </Button>
        )}
      </div>
    </div>
  );
}

export default ConfirmFocusCard;
